// Types for Statement Editor Component v6

import { BaseUISchema } from '../common-types'
import { ValidationConfig } from '../DataTableComponent/types-v4'
import { JsonFormPropertyV2, JsonFormSchemaV2 } from '../FormComponent/types'

// Main response type
export interface StatementEditorResponse {
  uiSchema: UISchema | Record<string, never>
  dataSchema: DataSchema | Record<string, never>
  filters: JsonFormSchemaV2 | Record<string, never>
}

// UI Schema types
export interface UISchema extends BaseUISchema {
  permissions: StatementEditorPermissions
  columns: number
  handlerId?: string
  fields: FieldDefinition[]
}

export interface StatementEditorPermissions {
  canEditContent: boolean
  canDeleteContent: boolean
}

export interface FieldDefinition {
  propertyId: string
  title: string
  description: string
  fieldConfig: FieldConfig
  handlerId?: string
  fields?: FieldDefinition[] // For nested fields like statements
}

export interface FieldConfig {
  layout: LayoutConfig
  editing: EditingConfig
}

export interface LayoutConfig {
  position: 'columns' | 'header' | 'content'
  component?: 'Icon' | 'Badge'
  columnLayout?: ColumnLayout
  metadata?: Record<string, MetadataConfig>
}

export interface ColumnLayout {
  colStart: number
  colSpan: number
}

export interface MetadataConfig {
  color: string
  icon?: string
}

// export interface EditingConfig {
//   editable: boolean
//   type: string
//   choices?: string[]
// }
export interface EditingConfig extends Omit<JsonFormPropertyV2, 'id' | 'title' | 'description'> {
  editable: boolean
  required?: boolean
  generateOnCreate?: boolean
  validation?: ValidationConfig
}

// Data Schema types
export interface DataSchema {
  indexData: ChapterNode[]
  contentData: ContentDataType[]
}

// Node types for the index hierarchy
export interface BaseNode {
  id: string
  type: string
  title: string
  description: string
}

export interface ChapterNode extends BaseNode {
  children: SectionNode[]
}

export interface SectionNode extends BaseNode {
  children?: TopicNode[]
  contentRefs?: string[] // Some sections might directly reference content
}

export interface TopicNode extends BaseNode {
  contentRefs: string[]
}

// Helper type for lookups
export type NodeType = 'chapter' | 'section' | 'topic'

// Union type for all node types
export type IndexNode = ChapterNode | SectionNode | TopicNode

// Dynamic types for content data based on UI schema fields
export type ContentDataType = {
  id: string
} & Record<string, unknown>

// Helper type to create dynamic object type based on field definitions
export type InferFieldType<T extends FieldDefinition> = T extends { fieldConfig: { editing: { type: 'string' } } }
  ? string
  : T extends { fieldConfig: { editing: { type: 'number' } } }
    ? number
    : T extends { fieldConfig: { editing: { type: 'boolean' } } }
      ? boolean
      : T extends { fieldConfig: { editing: { type: 'array' } }; fields: infer F }
        ? F extends FieldDefinition[]
          ? Array<InferContentType<F[number]>>
          : unknown[]
        : unknown

// Type helper to build a content object from field definitions
export type InferContentType<T extends FieldDefinition> = {
  [K in T['propertyId']]: InferFieldType<Extract<T, { propertyId: K }>>
}

// Type to dynamically build content data from UI schema fields
export type DynamicContentData<T extends FieldDefinition[]> = {
  id: string
} & {
  [K in T[number]['propertyId']]?: Extract<T[number], { propertyId: K }> extends { fields: infer F }
    ? F extends FieldDefinition[]
      ? Array<InferContentType<F[number]>>
      : unknown
    : InferFieldType<Extract<T[number], { propertyId: K }>>
}

// Type guard utility functions
export function isUISchema(value: UISchema | Record<string, never>): value is UISchema {
  return (
    typeof value === 'object' && value !== null && 'permissions' in value && 'columns' in value && 'fields' in value
  )
}

export function isDataSchema(value: DataSchema | Record<string, never>): value is DataSchema {
  return typeof value === 'object' && value !== null && 'indexData' in value && 'contentData' in value
}

export function isJsonFormSchema(value: JsonFormSchemaV2 | Record<string, never>): value is JsonFormSchemaV2 {
  return typeof value === 'object' && value !== null && 'properties' in value
}

export function isEmptyObject(value: unknown): value is Record<string, never> {
  return typeof value === 'object' && value !== null && Object.keys(value).length === 0
}

// Example usage
// function processContent(uiSchema: UISchema, data: DataSchema) {
//   // Dynamically typed content based on UI schema
//   type ContentType = DynamicContentData<typeof uiSchema.fields>

//   // Now when you access content, it will have the correct types
//   const typedContent = data.contentData as ContentType[]

//   // TypeScript will know the shape based on UI schema
//   // For example, if UI schema defines a field "originalText", this will be type-safe
//   const text = typedContent[0].originalText
// }

// // Example usage with type guards
// function processStatementEditorResponse(response: StatementEditorResponse) {
//   if (isUISchema(response.uiSchema)) {
//     // TypeScript knows this is UISchema, not empty object
//     console.log('UI Schema title:', response.uiSchema.title)
//     console.log('Can edit content:', response.uiSchema.permissions.canEditContent)
//   }

//   if (isDataSchema(response.dataSchema)) {
//     // TypeScript knows this is DataSchema, not empty object
//     console.log('Index data count:', response.dataSchema.indexData.length)
//     console.log('Content data count:', response.dataSchema.contentData.length)
//   }

//   if (isJsonFormSchema(response.filters)) {
//     // TypeScript knows this is JsonFormSchemaV2, not empty object
//     console.log('Filter properties:', Object.keys(response.filters.properties))
//   }
// }
